// src/lib/thermo/eos/PRSV.js
// Peng–Robinson EOS with PRSV alpha; mixing rules with optional kij.
// Provides: solvePRSV(), fugacityCoefficients(), departureEnthalpy().

const R = 8.314462618; // J/mol/K

// --- PRSV alpha (kappa per Stryjek–Vera) ---
function kappaPRSV(omega) {
  // Common 3-term fit (very decent over typical hydrocarbon ω)
  // κ = 0.378893 + 1.4897153 ω − 0.17131848 ω^2 + 0.0196554 ω^3
  const w = Math.max(-0.15, Math.min(1.2, omega ?? 0));
  return 0.378893 + 1.4897153 * w - 0.17131848 * w * w + 0.0196554 * w * w * w;
}

function alphaPRSV(T, Tc, omega) {
  const Tr = Math.max(1e-6, T / Tc);
  const k = kappaPRSV(omega);
  const term = 1 + k * (1 - Math.sqrt(Tr));
  return term * term;
}

// --- Pure PR (a,b) at given T (with PRSV alpha) ---
function purePRParams(comp, T) {
  const { Tc, Pc, omega } = comp;
  const a0 = (0.45724 * R * R * Tc * Tc) / Pc;
  const b = (0.0778 * R * Tc) / Pc;
  const alpha = alphaPRSV(T, Tc, omega);
  return { a: a0 * alpha, b, a0, alpha };
}

// --- Quadratic mixing (vdW-1) with kij ---
function mixParams(z, a_i, b_i, kij) {
  const n = z.length;
  let a_mix = 0;
  let b_mix = 0;
  for (let i = 0; i < n; i++) {
    b_mix += z[i] * b_i[i];
  }
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      a_mix += z[i] * z[j] * Math.sqrt(a_i[i] * a_i[j]) * (1 - kij_ij);
    }
  }
  return { a_mix, b_mix };
}

// --- Solve cubic for Z (PR) : Z^3 + c2 Z^2 + c1 Z + c0 = 0 ---
function solveCubicPR(A, B) {
  // PR form:
  // Z^3 - (1 - B) Z^2 + (A - 3B^2 - 2B) Z - (A B - B^2 - B^3) = 0
  const c2 = -(1 - B);
  const c1 = A - 3 * B * B - 2 * B;
  const c0 = -(A * B - B * B - B * B * B);

  // Depressed cubic via Cardano
  const a = 1;
  const b = c2;
  const c = c1;
  const d = c0;

  const p = (3 * a * c - b * b) / (3 * a * a);
  const q = (2 * b * b * b - 9 * a * b * c + 27 * a * a * d) / (27 * a * a * a);
  const Δ = (q * q) / 4 + (p * p * p) / 27;

  const roots = [];
  if (Δ > 0) {
    const u = Math.cbrt(-q / 2 + Math.sqrt(Δ));
    const v = Math.cbrt(-q / 2 - Math.sqrt(Δ));
    roots.push(u + v - b / (3 * a));
  } else if (Math.abs(Δ) < 1e-16) {
    const u = Math.cbrt(-q / 2);
    roots.push(2 * u - b / (3 * a));
    roots.push(-u - b / (3 * a));
  } else {
    const r = Math.sqrt((-p * p * p) / 27);
    const phi = Math.acos(-q / (2 * Math.sqrt((-p * p * p) / 27)));
    const m = 2 * Math.sqrt(-p / 3);
    roots.push(m * Math.cos(phi / 3) - b / (3 * a));
    roots.push(m * Math.cos((phi + 2 * Math.PI) / 3) - b / (3 * a));
    roots.push(m * Math.cos((phi + 4 * Math.PI) / 3) - b / (3 * a));
  }

  return roots.filter((Z) => isFinite(Z));
}

// --- Fugacity coefficients (phi_i) for PR mixture at Z ---
function fugacityCoefficientsAtZ(Z, z, a_i, b_i, a_mix, b_mix, A, B, kij) {
  const n = z.length;
  const sqrt_ai = a_i.map((ai) => Math.sqrt(ai));

  // a_ij matrix once
  const a_ij = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      a_ij[i][j] = sqrt_ai[i] * sqrt_ai[j] * (1 - kij_ij);
    }
  }
  // da_mix/ d n_i ≈ 2 Σ z_j a_ij  (since we treat z as mole fractions)
  const da_mix = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let j = 0; j < n; j++) s += z[j] * a_ij[i][j];
    da_mix[i] = 2 * s;
  }

  const phi = new Array(n);
  for (let i = 0; i < n; i++) {
    const bi_over_b = b_i[i] / b_mix;
    // Standard PR/PRSV ln(phi_i):
    //   ln(phi_i) = (b_i/b)(Z-1) - ln(Z-B)
    //              - (A/(2√2 B)) * ( 2 Σ_j x_j a_ij / a - b_i/b ) * ln( (Z+(1+√2)B)/(Z+(1-√2)B) )
    // NOTE: Do NOT include an extra "-ln(1 - B/Z)" term (it double-counts ln(Z-B))
    // and explodes φ near Z≈B.
    const lnTerm1 = bi_over_b * (Z - 1) - Math.log(Math.max(1e-12, Z - B));

    const termNum = Z + (1 + Math.SQRT2) * B;
    const termDen = Z + (1 - Math.SQRT2) * B;
    const lnArg = Math.max(1e-12, termNum / termDen);

    const term2 =
      (A / (2 * Math.SQRT2 * B)) *
      (da_mix[i] / a_mix - b_i[i] / b_mix) *
      Math.log(lnArg);

    const lnPhi = lnTerm1 - term2; // subtract attraction term
    phi[i] = Math.exp(Math.max(-50, Math.min(50, lnPhi)));
  }
  return phi;
}

// --- Departure enthalpy (per mole) for PR mixture at Z (J/mol) ---
function departureEnthalpyAtZ(T, P, Z, z, a_i, b_i, a_mix, b_mix, A, B, kij) {
  // For PR:
  // h_dep = R T (Z - 1) + (T da/dT - a) / (2*sqrt2*b) * ln((Z + (1+√2)B)/(Z + (1-√2)B))
  // where da/dT is mixture derivative at constant composition.
  const n = z.length;
  const sqrt_ai = a_i.map((ai) => Math.sqrt(ai));

  // Build a_ij(T) again (same as in fugacity)
  const a_ij = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      a_ij[i][j] = sqrt_ai[i] * sqrt_ai[j] * (1 - kij_ij);
    }
  }

  // Need da_i/dT for each i
  // a_i = a0_i * alpha(T), with a0_i const; d a_i/dT = a0_i * d alpha/dT
  // For PRSV alpha(T) = (1 + κ (1 - sqrt(Tr)))^2, Tr=T/Tc
  function dalpha_dT(T, Tc, omega) {
    const Tr = T / Tc;
    const k = kappaPRSV(omega);
    const g = 1 + k * (1 - Math.sqrt(Tr));
    const dg_dT = ((k * -0.5) / Math.sqrt(Tr)) * (1 / Tc);
    return 2 * g * dg_dT; // d/dT g^2 = 2 g dg/dT
  }

  // a_i(T) and d a_i/dT
  // (We already have a_i. Rebuild a0_i to get derivative cleanly.)
  const a0_i = z.map((_, i) => {
    const { Tc, Pc } = z.__comps[i];
    return (0.45724 * R * R * Tc * Tc) / Pc;
  });
  const da_i = z.map((_, i) => {
    const { Tc, omega } = z.__comps[i];
    return a0_i[i] * dalpha_dT(T, Tc, omega);
  });

  // da_mix/dT = Σ_i Σ_j z_i z_j * 1/2 * ( (da_i/√a_i)√a_j + √a_i (da_j/√a_j) ) (1 - kij)
  // Simplifies to Σ_i Σ_j z_i z_j * 0.5 * ( (da_i/ai) + (da_j/aj) ) * sqrt(ai aj) * (1 - kij)
  let da_mix = 0;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      const term =
        0.5 *
        (da_i[i] / a_i[i] + da_i[j] / a_i[j]) *
        Math.sqrt(a_i[i] * a_i[j]) *
        (1 - kij_ij);
      da_mix += z[i] * z[j] * term;
    }
  }

  const logNum = Z + (1 + Math.SQRT2) * B;
  const logDen = Z + (1 - Math.SQRT2) * B;
  const lnArg = Math.log(Math.max(1e-12, logNum / logDen));

  const term1 = R * T * (Z - 1);
  const term2 = ((T * da_mix - a_mix) / (2 * Math.SQRT2 * b_mix)) * lnArg;

  return term1 + term2; // J/mol
}

// --- Main entry: compute Z roots, select phases, phis, hdep ---
export function solvePRSV({ P, T, x, comps, kij }) {
  // x: composition vector (mole fractions) tagged with comps for derivative usage
  const n = x.length;
  const a_i = new Array(n);
  const b_i = new Array(n);

  for (let i = 0; i < n; i++) {
    const { a, b } = purePRParams(comps[i], T);
    a_i[i] = a;
    b_i[i] = b;
  }

  const { a_mix, b_mix } = mixParams(x, a_i, b_i, kij);
  const A = (a_mix * P) / (R * R * T * T);
  const B = (b_mix * P) / (R * T);

  const epsZ = 1e-9;
  const rootsAll = solveCubicPR(A, B).filter((Z) => Number.isFinite(Z));
  const roots = rootsAll.filter((Z) => Z > Math.max(0, B + epsZ));
  roots.sort((a, b) => a - b);

  let ZL, ZV;
  let singlePhase = false;
  let Zsingle = null;
  if (roots.length >= 2) {
    ZL = roots[0];
    ZV = roots[roots.length - 1];
  } else if (roots.length === 1) {
    singlePhase = true;
    Zsingle = roots[0];
    ZL = ZV = Zsingle;
  } else {
    // fallback: treat as single-phase and use the most reasonable root we can find
    singlePhase = true;
    const Zcand = rootsAll.length ? Math.max(...rootsAll) : 1.0;
    Zsingle = Math.max(Math.max(1e-6, B + 1e-6), Zcand);
    ZL = ZV = Zsingle;
  }
  // If roots collapse to essentially the same value, treat as single-phase
  if (!singlePhase) {
    const rel = Math.abs(ZV - ZL) / Math.max(1e-12, Math.abs(ZV));
    if (rel < 1e-8) {
      singlePhase = true;
      Zsingle = 0.5 * (ZL + ZV);
      ZL = ZV = Zsingle;
    }
  }

  // Tag comps to x for enthalpy derivatives (local hack to avoid passing comps everywhere)
  x.__comps = comps;

    if (singlePhase) {
    const phi = fugacityCoefficientsAtZ(
      Zsingle,
      x,
      a_i,
      b_i,
      a_mix,
      b_mix,
      A,
      B,
      kij
    );
    return {
      singlePhase: true,
      Z: Zsingle,
      ZL: Zsingle,
      ZV: Zsingle,
      phiL: phi,
      phiV: phi,
      hdepL: 0,
      hdepV: 0,
      a_i,
      b_i,
      a_mix,
      b_mix,
    };
  }

const phiL = fugacityCoefficientsAtZ(
    ZL,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    kij
  );
  const phiV = fugacityCoefficientsAtZ(
    ZV,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    kij
  );

  const hdepL = departureEnthalpyAtZ(
    T,
    P,
    ZL,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    kij
  );
  const hdepV = departureEnthalpyAtZ(
    T,
    P,
    ZV,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    kij
  );

  return { ZL, ZV, phiL, phiV, hdepL, hdepV, a_i, b_i, a_mix, b_mix };
}
